import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { createCollegeNote, fetchCollegeNotes, deleteCollegeNote, updateCollegeNote } from '../services/collegeNotesService';
import EditIcon from '../images/buttons/Edit.svg';
import DeleteIcon from '../images/buttons/Delete.svg';
import '../styles/college-notes-panel.css';

interface Note {
  id: number;
  name: string;
  content: string;
  date: string;
}

interface CollegeNotesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  collegeName: string;
  collegeId: number | null;
  athleteId: number | null;
  notes: Note[];
  onAddNote: (note: Note) => void;
  onUpdateNote?: (note: Note) => void;
  onDeleteNote: (noteId: number) => void;
  onNotesLoaded?: (collegeId: number, notes: Note[]) => void;
  /** Guardians and other read-only viewers can load notes but not create/delete. */
  readOnly?: boolean;
}

const CollegeNotesPanel: React.FC<CollegeNotesPanelProps> = ({
  isOpen,
  onClose,
  collegeName,
  collegeId,
  athleteId,
  notes,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  onNotesLoaded,
  readOnly = false,
}) => {
  const [noteName, setNoteName] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [deletingNoteId, setDeletingNoteId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);

  const friendlyError = (err: unknown, fallback: string) => {
    const message = err instanceof Error ? err.message : fallback;
    if (/exception|sql|stack trace/i.test(message)) {
      return fallback;
    }
    return message || fallback;
  };

  // Fetch notes when panel opens
  useEffect(() => {
    const loadNotes = async () => {
      if (!isOpen || !athleteId || !collegeId) {
        return;
      }

      // Only fetch if we don't have notes loaded yet
      if (notes.length > 0) {
        return;
      }

      setIsLoadingNotes(true);
      setFetchError(null);

      try {
        const fetchedNotes = await fetchCollegeNotes(athleteId, collegeId);

        // Transform API response to match Note interface
        const transformedNotes: Note[] = fetchedNotes
          .map(note => ({
            id: note.id,
            name: note.name || 'Untitled Note',
            content: note.body,
            date: note.createdAt
          }))
          // Sort by date descending (most recent first)
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        // Pass loaded notes to parent
        if (onNotesLoaded) {
          onNotesLoaded(collegeId, transformedNotes);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load notes';
        setFetchError(errorMessage);
      } finally {
        setIsLoadingNotes(false);
      }
    };

    loadNotes();
  }, [isOpen, athleteId, collegeId, notes.length]);

  const handleAddNote = async () => {
    if (readOnly) {
      return;
    }
    if (!noteName.trim() || !noteContent.trim()) {
      return;
    }

    if (!athleteId || !collegeId) {
      setError('Missing athlete or college information');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (editingNoteId != null) {
        const response = await updateCollegeNote(athleteId, editingNoteId, noteName.trim(), noteContent.trim());
        const updatedNote: Note = {
          id: response.id,
          name: response.name,
          content: response.body,
          date: response.createdAt
        };
        onUpdateNote?.(updatedNote);
        setEditingNoteId(null);
      } else {
        const response = await createCollegeNote(athleteId, collegeId, noteName.trim(), noteContent.trim());
        const newNote: Note = {
          id: response.id,
          name: response.name,
          content: response.body,
          date: response.createdAt
        };
        onAddNote(newNote);
      }

      setNoteName('');
      setNoteContent('');
    } catch (err) {
      setError(friendlyError(err, editingNoteId != null ? 'Could not save that note.' : 'Failed to create note'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEdit = (note: Note) => {
    setEditingNoteId(note.id);
    setNoteName(note.name);
    setNoteContent(note.content);
    setError(null);
  };

  const cancelEdit = () => {
    setEditingNoteId(null);
    setNoteName('');
    setNoteContent('');
    setError(null);
  };

  const handleDeleteNote = async (noteId: number) => {
    if (readOnly) {
      return;
    }
    if (!athleteId) {
      setDeleteError('Missing athlete information');
      return;
    }

    setDeletingNoteId(noteId);
    setDeleteError(null);

    try {
      // Call the API to delete the note
      await deleteCollegeNote(athleteId, noteId);

      // Update UI by calling parent's delete handler
      onDeleteNote(noteId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete note';
      setDeleteError(errorMessage);
    } finally {
      setDeletingNoteId(null);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="notes-panel-overlay" data-testid="notes-panel-overlay" />
      <div className="notes-panel" data-testid="notes-panel">
        <div className="notes-panel-header">
          <button
            className="notes-panel-close"
            onClick={onClose}
            aria-label="Close panel"
            data-testid="notes-panel-close"
          >
            ✕
          </button>
          <h2 className="notes-panel-title">Notes: <span className="notes-panel-college-name">{collegeName}</span></h2>
          <p className="notes-panel-description">
            {readOnly
              ? "Notes about this college search or recruitment activity."
              : "Add a brief note about your college search or recruitment activity — such as outreach, visits, conversations, or updates."}
          </p>
        </div>

        <div className="notes-panel-content">
          {error && (
            <div className="notes-error-message" data-testid="notes-error-message">
              {error}
            </div>
          )}
          {!readOnly && (
          <div className="notes-input-section">
            <input
              type="text"
              className="note-name-input"
              placeholder="Note name"
              value={noteName}
              onChange={(e) => setNoteName(e.target.value)}
              data-testid="note-name-input"
            />
            <textarea
              className="note-content-input"
              placeholder="Add note"
              rows={4}
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              data-testid="note-content-input"
            />
            <div className="notes-form-actions">
            <button
              className="add-note-btn-panel"
              onClick={handleAddNote}
              disabled={!noteName.trim() || !noteContent.trim() || isSubmitting}
              data-testid={editingNoteId != null ? 'save-note-btn' : 'add-note-btn'}
            >
              <span className="add-note-icon">⊕</span>{' '}
              {isSubmitting
                ? 'Saving...'
                : editingNoteId != null
                  ? 'Save changes'
                  : 'Add note'}
            </button>
            {editingNoteId != null && (
              <button
                type="button"
                className="note-cancel-btn"
                onClick={cancelEdit}
                data-testid="cancel-note-edit"
              >
                Cancel
              </button>
            )}
            </div>
          </div>
          )}

          <div className="notes-log-section">
            <h3 className="notes-log-title">Note log</h3>
            {deleteError && (
              <div className="notes-error-message" data-testid="delete-error-message">
                {deleteError}
              </div>
            )}
            {isLoadingNotes ? (
              <div className="notes-loading" data-testid="notes-loading">
                Loading notes...
              </div>
            ) : fetchError ? (
              <div className="notes-fetch-error" data-testid="notes-fetch-error">
                {fetchError}
              </div>
            ) : (
              <div className="notes-list" data-testid="notes-list">
                {notes.length === 0 ? (
                  <p className="no-notes-message">
                    {readOnly ? "No notes yet." : "No notes yet. Add your first note above."}
                  </p>
                ) : (
                  notes.map((note) => (
                    <div key={note.id} className="note-item" data-testid={`note-item-${note.id}`}>
                      <div className="note-item-header">
                        <h4 className="note-item-title">{note.name}</h4>
                        {!readOnly && (
                        <div className="note-item-actions">
                          <button
                            type="button"
                            className="note-item-icon-btn"
                            onClick={() => startEdit(note)}
                            aria-label="Edit note"
                            title="Edit note"
                            data-testid={`edit-note-${note.id}`}
                          >
                            <img src={EditIcon} alt="" />
                          </button>
                          <button
                            type="button"
                            className="note-item-icon-btn"
                            onClick={() => handleDeleteNote(note.id)}
                            disabled={deletingNoteId === note.id}
                            aria-label="Delete note"
                            title="Delete note"
                            data-testid={`delete-note-${note.id}`}
                          >
                            <img src={DeleteIcon} alt="" />
                          </button>
                        </div>
                        )}
                      </div>
                      <p className="note-item-content">{note.content}</p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default CollegeNotesPanel;

